import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useDispatch } from 'react-redux'
import { setParticipant } from '../../store/participant/participant'

interface RadioOption {
  value: boolean
  label: string
  icon: string
  colorClass: 'success' | 'danger'
}

interface EnhancedRadioGroupProps {
  name: string
  participant: any
  options: RadioOption[]
  className?: string
  required?: boolean
}

export interface EnhancedRadioGroupRef {
  triggerAnimation: () => void
  scrollIntoView: () => void
}

const EnhancedRadioGroup = forwardRef<EnhancedRadioGroupRef, EnhancedRadioGroupProps>(
  ({ name, participant, options, className = '', required = false }, ref) => {
    const dispatch = useDispatch()
    const [showAnimation, setShowAnimation] = useState(false)
    const firstInputRef = useRef<HTMLInputElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    useImperativeHandle(ref, () => ({
      triggerAnimation: () => {
        setShowAnimation(true)
        setTimeout(() => setShowAnimation(false), 1000)
      },
      scrollIntoView: () => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }))

    const handleChange = (value: boolean) => {
      dispatch(
        setParticipant({
          ...participant,
          [name]: value,
        })
      )
    }

    const currentValue = participant[name]

    return (
      <div 
        ref={containerRef}
        className={`photo-permission-radio-group ${className} ${showAnimation ? 'animate-attention' : ''}`}
      >
        {options.map((option, index) => (
          <label 
            key={option.value.toString()} 
            className={`radio-option ${currentValue === option.value ? `selected ${option.colorClass}` : ''}`}
          >
            <input
              ref={index === 0 ? firstInputRef : undefined}
              type="radio"
              name={name}
              value={option.value.toString()}
              checked={currentValue === option.value}
              onChange={() => handleChange(option.value)}
              required={required}
            />
            <div className="radio-content">
              <FontAwesomeIcon 
                icon={option.icon as any} 
                className={`radio-icon ${option.colorClass}-icon`} 
              />
              <span className="radio-text">{option.label}</span>
            </div>
          </label>
        ))}
      </div>
    )
  }
)

EnhancedRadioGroup.displayName = 'EnhancedRadioGroup'

export default EnhancedRadioGroup
